import { useCallback, useMemo, useState } from 'react';
import { bomClient } from '../client/bomClient';
import { useRFQ } from '../contexts/RFQContext';
import { SelectedLines } from '../types/discovery.types';
import { CreditGate, GateLine, evaluateCreditGate } from '../utils/creditGate';
import { discoveryErrorCode, selectionErrorMessage } from './bomCredits';
import { BomPricing } from './useBomPricing';
import { useRecordRobbieAction } from '../contexts/RobbieUiContext';
import { robbieActionText } from '../utils/robbieActivity';

/**
 * The step-2 entry credit gate. On entering sourcing criteria, a balance that
 * cannot cover discovery for every line still to be paid for closes the step
 * behind a picker (see `evaluateCreditGate` for the rule). The gate is
 * DERIVED from what is on screen — the lines, what is paid for, the rate and
 * the balance — so it re-checks itself whenever any of them changes: a line
 * added in the sheet, a balance that moved. While the rate or balance is still
 * loading it reports `pending` instead of "not needed", so the step stays shut
 * rather than opening and then being covered; a read that FAILED settles it to
 * unknown and unlocks the step without gating.
 *
 * Keeping lines trims the BOM on the server without charging, then reloads the
 * RFQ; the gate re-evaluates on the trimmed lines and unlocks the step. The
 * exit picker stays as the safety net for a balance that moves later.
 *
 * Contract: docs/flows/02-bom-edit-and-discovery-charge.md, "Entering step 2".
 */

interface Options {
  rfqId: string;
  components: GateLine[];
  paidComponentIds: string[];
  pricing: BomPricing;
  readOnly: boolean;
}

export interface KeptNotice {
  title: string;
  message: string;
}

const keptNotice = ({ totalComponents, removedComponentIds }: SelectedLines): KeptNotice => {
  const removed = removedComponentIds.length;
  if (removed === 0) return { title: `Kept all ${totalComponents} lines`, message: 'Nothing left this BOM.' };
  return {
    title: `Kept ${totalComponents} of ${totalComponents + removed} lines`,
    message: removed === 1 ? 'The other line left this BOM.' : `The other ${removed} left this BOM.`,
  };
};

export const useStep2CreditGate = ({ rfqId, components, paidComponentIds, pricing, readOnly }: Options) => {
  const { fetchRFQ } = useRFQ();
  const recordAction = useRecordRobbieAction();
  const { rate, balance, pending } = pricing;

  const gate: CreditGate = useMemo(
    () => evaluateCreditGate({ components, paidComponentIds, rate, balance, pending, readOnly }),
    [components, paidComponentIds, rate, balance, pending, readOnly]
  );

  const [keeping, setKeeping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<KeptNotice | null>(null);
  /** Bumped when the BOM changed under the buyer, so the picker starts from a clean pick. */
  const [round, setRound] = useState(0);

  /** Reload the RFQ without moving the wizard. */
  const reload = useCallback(() => fetchRFQ(rfqId, true, true), [fetchRFQ, rfqId]);

  const keep = useCallback(
    async (componentIds: string[]) => {
      setError(null);
      setKeeping(true);
      try {
        const result = await bomClient.selectBomLines(rfqId, componentIds);
        recordAction('keepLines', robbieActionText.keptLines(result.totalComponents, result.removedComponentIds.length));
        await reload();
        setNotice(keptNotice(result));
      } catch (err) {
        setError(selectionErrorMessage(err));
        if (discoveryErrorCode(err) === 'UNKNOWN_COMPONENT') {
          setRound((current) => current + 1);
          try {
            await reload();
          } catch (reloadError) {
            // No structured logger in the browser yet; this is the one trace of it.
            // eslint-disable-next-line no-console
            console.warn('[credit gate] RFQ reload after a changed BOM failed', reloadError);
          }
        }
      } finally {
        setKeeping(false);
      }
    },
    [rfqId, reload, recordAction]
  );

  return {
    gate,
    keep,
    keeping,
    /** A failed keep, shown inside the picker (which stays open). */
    error,
    /** "Kept 10 of 13 lines" — shown once the gate has closed. */
    notice,
    dismissNotice: useCallback(() => setNotice(null), []),
    round,
  };
};

export type Step2CreditGate = ReturnType<typeof useStep2CreditGate>;

export default useStep2CreditGate;
